import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CurrencyChangeWizard } from '@/components/settings/CurrencyChangeWizard';
import { CurrencyHistoryTimeline } from '@/components/settings/CurrencyHistoryTimeline';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useToast } from '@/hooks/use-toast';
import {
  User, Bell, Palette, Shield, AlertTriangle, Download, KeyRound, Mail,
  Monitor, Sun, Moon, Smartphone, Laptop, Globe, Link, Link2Off,
  History, LogOut, Loader2, Chrome, Check, Clock, MapPin, Wand2,
  Copy, Pencil, Rows3, Trash2, Undo2,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import {
  changePassword, updateProfile, deleteAccountAction,
  googleLogin, unlinkGoogleAction,
} from '@/features/auth/authSlice';
import { ExportDialog } from '@/components/settings/ExportDialog';
import { ImportDialog } from '@/components/settings/ImportDialog';
import { TimezoneSelector } from '@/components/TimezoneSelector';
import { useNavigate } from 'react-router-dom';
import api, { endpoints } from '@/services/api';
import type { ApiResponse } from '@/services/api';
import { useAutomationPreferences } from '@/hooks/useAutomationPreferences';
import { fetchReceiptStats } from '@/features/receipts/receiptsSlice';
import type { FocusPageKey, FocusPresetProfile, PerPageFocusModes, Session, SecurityEvent } from '@/lib/types';
import { useTheme } from '@/components/theme/ThemeProvider';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { ThemeCustomizationPanel } from '@/components/settings/ThemeCustomizationPanel';
import { useGoogleLogin } from '@react-oauth/google';
// NOTE: useGoogleLogin is only used inside GoogleConnectSection (below), NOT in the
// main Settings component, to prevent hook initialisation errors when
// VITE_GOOGLE_CLIENT_ID is empty / GoogleOAuthProvider is absent.
import { formatDistanceToNow, format } from 'date-fns';
import PageGuide from '@/components/common/PageGuide';
import { settingsGuide } from '@/components/common/guideContent';
import { useIsMobile } from '@/hooks/use-mobile';
import { useItemCountPreference } from '@/hooks/useItemCountPreference';
import { FOCUS_PAGE_LABELS, FOCUS_PAGE_ORDER, formatRelativeTime, readLegacyFocusModes, resolveEffectiveFocusModes } from '@/lib/focusPresets';
import DataHealthPanel from '@/components/health/DataHealthPanel';

const PRESET_USAGE_KEY_PREFIX = 'ft-preset-usage-';

function getPresetUsageStorageKey(presetId: string): string {
  return `${PRESET_USAGE_KEY_PREFIX}${presetId}`;
}

// ─── Sub-component: Google Connect / Disconnect ─────────────────────────────
// Intentionally isolated so that `useGoogleLogin` is only called when this
// component is actually mounted (i.e. googleEnabled === true AND
// GoogleOAuthProvider is wrapping the tree).
interface GoogleConnectSectionProps {
  isConnected: boolean;
  hasLocalPassword: boolean;
}

function GoogleConnectSection({ isConnected, hasLocalPassword }: GoogleConnectSectionProps) {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [unlinkLoading, setUnlinkLoading] = useState(false);

  const handleConnect = useGoogleLogin({
    onSuccess: async ({ access_token }) => {
      setLoading(true);
      try {
        await dispatch(googleLogin(access_token)).unwrap();
        toast({ title: 'Google connected', description: 'Your Google account has been linked.' });
      } catch (e: unknown) {
        toast({ title: 'Error', description: (e as string) || 'Failed to connect Google', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    },
    onError: () =>
      toast({ title: 'Error', description: 'Google sign-in was cancelled or failed', variant: 'destructive' }),
    scope: 'email profile openid',
  });

  const handleUnlink = async () => {
    setUnlinkLoading(true);
    try {
      await dispatch(unlinkGoogleAction()).unwrap();
      toast({ title: 'Google disconnected', description: 'Your Google account has been unlinked.' });
    } catch (e: unknown) {
      toast({ title: 'Error', description: (e as string) || 'Failed to unlink Google', variant: 'destructive' });
    } finally {
      setUnlinkLoading(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border bg-card">
      <div className="flex items-center gap-3 min-w-0">
        {/* Google logo */}
        <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        <div>
          <p className="text-sm font-medium">Google</p>
          <p className="text-xs text-muted-foreground">
            {isConnected ? 'Connected — sign in with your Google account' : 'Not connected'}
          </p>
        </div>
      </div>
      {isConnected ? (
        <Button
          variant="outline"
          size="sm"
          onClick={handleUnlink}
          disabled={unlinkLoading || !hasLocalPassword}
          title={!hasLocalPassword ? 'Set a password first to disconnect Google' : undefined}
        >
          {unlinkLoading
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <><Link2Off className="h-4 w-4 mr-1" />Disconnect</>}
        </Button>
      ) : (
        <Button variant="outline" size="sm" onClick={() => handleConnect()} disabled={loading}>
          {loading
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <><Chrome className="h-4 w-4 mr-1" />Connect</>}
        </Button>
      )}
    </div>
  );
}

// ─── Helper: device icon ──────────────────────────────────────────────────────
function DeviceIcon({ deviceType }: { deviceType?: string }) {
  if (deviceType === 'mobile' || deviceType === 'tablet')
    return <Smartphone className="h-4 w-4 text-muted-foreground" />;
  return <Laptop className="h-4 w-4 text-muted-foreground" />;
}

// ─── Helper: security event badge ────────────────────────────────────────────
function EventBadge({ event }: { event: SecurityEvent }) {
  if (!event.success)
    return <Badge variant="destructive" className="text-xs shrink-0">Failed</Badge>;
  if (event.eventType.includes('password'))
    return <Badge variant="secondary" className="text-xs shrink-0 bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400">Password</Badge>;
  if (event.eventType.includes('logout'))
    return <Badge variant="outline" className="text-xs shrink-0">Logout</Badge>;
  return <Badge variant="secondary" className="text-xs shrink-0 bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-400">Login</Badge>;
}

// ─── Main Component ───────────────────────────────────────────────────────────
const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatNumber = (n: number): string => new Intl.NumberFormat('en-US').format(n ?? 0);

interface DeletePreview {
  totalRecords: number;
  receiptFileBytes: number;
  collections: {
    transactions: number;
    accounts: number;
    budgets: number;
    goals: number;
    receipts: number;
    subscriptions: number;
    dashboards: number;
    [key: string]: number;
  };
}

function createPresetId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `preset-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function formatModeLabel(mode: string): string {
  return mode
    .split('-')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

export default function Settings() {  useDocumentTitle('Settings');  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const receiptStats = useAppSelector((state) => state.receipts.stats);
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  const isMobile = useIsMobile();

  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  const googleEnabled = Boolean(googleClientId);
  const isGoogleConnected = user?.authProviders?.includes('google') ?? false;
  const hasLocalPassword = user?.authProviders?.includes('local') ?? true;

  // ── Profile state ───────────────────────────────────────────────────────
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [currency] = useState(user?.preferences?.currency || 'USD');
  const [currencyWizardOpen, setCurrencyWizardOpen] = useState(false);
  const [dateFormat, setDateFormat] = useState(user?.preferences?.dateFormat || 'MM/DD/YYYY');
  const [budgetMethod, setBudgetMethod] = useState(user?.preferences?.budgetMethod || 'spending-plan');
  const [showLineItemsInList, setShowLineItemsInList] = useState(user?.preferences?.showLineItemsInList ?? false);
  const [userTimezone, setUserTimezone] = useState(user?.preferences?.timezone || 'UTC');
  const { showItemCount, toggleItemCount } = useItemCountPreference();
  const [profileLoading, setProfileLoading] = useState(false);

  // T155P4 — Automation preferences (localStorage-backed, no backend endpoint yet)
  const {
    autoCategorize,
    showSuggestions,
    requirePreview,
    setAutoCategorize,
    setShowSuggestions,
    setRequirePreview,
    resetAutomationPreferences,
  } = useAutomationPreferences();

  // ── Notifications state ─────────────────────────────────────────────────
  const [emailNotifications, setEmailNotifications] = useState(
    user?.preferences?.notifications?.emailNotifications ?? false,
  );
  const [budgetAlerts, setBudgetAlerts] = useState(
    user?.preferences?.notifications?.budgetAlerts ?? true,
  );
  const [billReminders, setBillReminders] = useState(
    user?.preferences?.notifications?.billReminders ?? true,
  );
  const [notifLoading, setNotifLoading] = useState(false);

  // ── Password state ──────────────────────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // ── Sessions state ──────────────────────────────────────────────────────
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);

  // ── Security events state ───────────────────────────────────────────────
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const [securityEventsLoading, setSecurityEventsLoading] = useState(false);

  // ── Other state ─────────────────────────────────────────────────────────
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletePreview, setDeletePreview] = useState<DeletePreview | null>(null);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [auditRetentionDays, setAuditRetentionDays] = useState(
    user?.preferences?.auditRetentionDays ?? 3,
  );
  const [legacyFocusModes, setLegacyFocusModes] = useState<Partial<Record<FocusPageKey, string>>>({});
  const [createPresetName, setCreatePresetName] = useState('');
  const [renamePresetId, setRenamePresetId] = useState<string | null>(null);
  const [renamePresetName, setRenamePresetName] = useState('');
  const [deletePresetId, setDeletePresetId] = useState<string | null>(null);
  const [previewPresetId, setPreviewPresetId] = useState<string | null>(null);
  const [presetActionLoading, setPresetActionLoading] = useState<string | null>(null);
  const [, setPresetUsageVersion] = useState(0);
  const [applyUndoState, setApplyUndoState] = useState<{
    activePresetId: string | null;
    perPageFocusModes: PerPageFocusModes;
    presetName: string;
  } | null>(null);

  // ── Fetch sessions ──────────────────────────────────────────────────────
  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await api.get<{ success: boolean; sessions: Session[] }>(endpoints.auth.sessions);
      setSessions(res.data.sessions || []);
    } catch {
      // silent — sessions unavailable
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  // ── Fetch security events ───────────────────────────────────────────────
  const loadSecurityEvents = useCallback(async () => {
    setSecurityEventsLoading(true);
    try {
      const res = await api.get<{ success: boolean; events: SecurityEvent[] }>(endpoints.auth.securityEvents);
      setSecurityEvents((res.data.events || []).slice(0, 10));
    } catch {
      // silent
    } finally {
      setSecurityEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
    loadSecurityEvents();
  }, [loadSessions, loadSecurityEvents]);

  useEffect(() => {
    dispatch(fetchReceiptStats());
  }, [dispatch]);

  useEffect(() => {
    setLegacyFocusModes(readLegacyFocusModes());
  }, []);

  const presetProfiles = useMemo(() => user?.preferences?.presetProfiles ?? [], [user?.preferences?.presetProfiles]);
  const activePresetId = user?.preferences?.activePresetId ?? null;
  const activePreset = useMemo(
    () => presetProfiles.find((preset) => preset.id === activePresetId) ?? null,
    [activePresetId, presetProfiles],
  );
  const effectiveFocusModes = useMemo(
    () =>
      resolveEffectiveFocusModes({
        perPageFocusModes: user?.preferences?.perPageFocusModes,
        activePreset,
        legacyModes: legacyFocusModes,
      }),
    [activePreset, legacyFocusModes, user?.preferences?.perPageFocusModes],
  );
  const previewPreset = useMemo(
    () => presetProfiles.find((preset) => preset.id === previewPresetId) ?? null,
    [presetProfiles, previewPresetId],
  );
  const previewModes = useMemo(
    () =>
      resolveEffectiveFocusModes({
        perPageFocusModes: previewPreset?.perPageFocusModes,
        activePreset: previewPreset,
      }),
    [previewPreset],
  );
  const hasLegacyPresetFallback = presetProfiles.length === 0 && Object.keys(legacyFocusModes).length > 0;

  const persistPresetPreferences = useCallback(
    async (preferences: Partial<NonNullable<typeof user>['preferences']>) => {
      await dispatch(updateProfile({ preferences })).unwrap();
    },
    [dispatch],
  );

  const getPresetLastApplied = useCallback((id: string): string | null => {
    try {
      return localStorage.getItem(getPresetUsageStorageKey(id));
    } catch {
      return null;
    }
  }, []);

  const markPresetApplied = useCallback((presetId: string) => {
    try {
      localStorage.setItem(getPresetUsageStorageKey(presetId), new Date().toISOString());
    } catch {
      // Ignore storage permission/quota failures.
    }
    setPresetUsageVersion((value) => value + 1);
  }, []);

  const clearPresetUsage = useCallback((presetId: string) => {
    try {
      localStorage.removeItem(getPresetUsageStorageKey(presetId));
    } catch {
      // Ignore storage permission/quota failures.
    }
    setPresetUsageVersion((value) => value + 1);
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleProfileSave = async () => {
    setProfileLoading(true);
    try {
      await dispatch(updateProfile({
        firstName,
        lastName,
        preferences: {
          currency,
          dateFormat,
          budgetMethod,
          showLineItemsInList,
          timezone: userTimezone,
          auditRetentionDays,
          automation: {
            autoCategorize,
            showSuggestions,
            requirePreview,
          },
        },
      })).unwrap();
      toast({ title: 'Profile updated', description: 'Your profile has been saved.' });
    } catch (e: unknown) {
      toast({ title: 'Error', description: (e as string) || 'Failed to update profile', variant: 'destructive' });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleNotificationsSave = async () => {
    setNotifLoading(true);
    try {
      await dispatch(updateProfile({
        preferences: { notifications: { emailNotifications, budgetAlerts, billReminders } },
      })).unwrap();
      toast({ title: 'Notifications updated', description: 'Your notification preferences have been saved.' });
    } catch (e: unknown) {
      toast({ title: 'Error', description: (e as string) || 'Failed to save notifications', variant: 'destructive' });
    } finally {
      setNotifLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast({ title: 'Error', description: 'New passwords do not match.', variant: 'destructive' });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: 'Error', description: 'Password must be at least 8 characters.', variant: 'destructive' });
      return;
    }
    setPasswordLoading(true);
    try {
      await dispatch(changePassword({ currentPassword, newPassword })).unwrap();
      toast({ title: 'Password changed', description: 'Your password has been updated.' });
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (e: unknown) {
      toast({ title: 'Error', description: (e as string) || 'Failed to change password', variant: 'destructive' });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    setRevokingId(sessionId);
    try {
      await api.delete(endpoints.auth.revokeSession(sessionId));
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      toast({ title: 'Session revoked', description: 'The session has been terminated.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to revoke session', variant: 'destructive' });
    } finally {
      setRevokingId(null);
    }
  };

  const handleLogoutAllOther = async () => {
    setLogoutAllLoading(true);
    try {
      await api.post(endpoints.auth.logoutAll);
      await loadSessions();
      toast({ title: 'Logged out', description: 'All other sessions have been terminated.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to logout other sessions', variant: 'destructive' });
    } finally {
      setLogoutAllLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await dispatch(deleteAccountAction({ confirmDelete: 'DELETE', password: deletePassword })).unwrap();
      toast({ title: 'Account deleted', description: 'Your account has been permanently deleted.' });
      setDeleteDialogOpen(false);
      window.location.href = '/';
    } catch (e: unknown) {
      toast({ title: 'Error', description: (e as string) || 'Failed to delete account', variant: 'destructive' });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Fetch deletion preview when the confirmation dialog opens (GDPR Art. 17 — informed consent)
  useEffect(() => {
    if (!deleteDialogOpen) {
      setDeletePreview(null);
      return;
    }
    let cancelled = false;
    setDeletePreviewLoading(true);
    api.get<ApiResponse<DeletePreview>>(endpoints.auth.deletePreview)
      .then((res) => {
        if (!cancelled) setDeletePreview(res.data.data);
      })
      .catch(() => {
        if (!cancelled) {
          toast({
            title: 'Preview unavailable',
            description: 'Could not load the deletion summary. You can still proceed.',
            variant: 'destructive',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setDeletePreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [deleteDialogOpen, toast]);

  const handleResendVerification = async () => {
    setVerificationLoading(true);
    try {
      await api.post(endpoints.auth.resendVerification);
      toast({ title: 'Verification sent', description: 'A verification email has been sent to your inbox.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to resend verification', variant: 'destructive' });
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleCreatePreset = useCallback(async (options?: { activate?: boolean; seedModes?: PerPageFocusModes; name?: string }) => {
    const nextName = (options?.name ?? createPresetName).trim();
    if (!nextName) {
      toast({ title: 'Preset name required', description: 'Name the preset before saving it.', variant: 'destructive' });
      return;
    }

    const seedModes = options?.seedModes ?? effectiveFocusModes;
    const nextPreset: FocusPresetProfile = {
      id: createPresetId(),
      name: nextName,
      perPageFocusModes: { ...seedModes },
    };

    setPresetActionLoading(`create:${nextPreset.id}`);
    try {
      await persistPresetPreferences({
        presetProfiles: [...presetProfiles, nextPreset],
        ...(options?.activate
          ? {
              activePresetId: nextPreset.id,
              perPageFocusModes: { ...nextPreset.perPageFocusModes },
            }
          : {}),
      });
      if (options?.activate) {
        setApplyUndoState({
          activePresetId,
          perPageFocusModes: { ...(user?.preferences?.perPageFocusModes ?? {}) },
          presetName: nextPreset.name,
        });
        markPresetApplied(nextPreset.id);
      }
      setCreatePresetName('');
      toast({
        title: options?.activate ? 'Portable preset created' : 'Preset saved',
        description: options?.activate
          ? `${nextPreset.name} is now active across your devices.`
          : `${nextPreset.name} is ready to apply whenever you need it.`,
      });
    } catch (error: unknown) {
      toast({ title: 'Preset save failed', description: String(error), variant: 'destructive' });
    } finally {
      setPresetActionLoading(null);
    }
  }, [activePresetId, createPresetName, effectiveFocusModes, markPresetApplied, persistPresetPreferences, presetProfiles, toast, user?.preferences?.perPageFocusModes]);

  const handleRenamePreset = useCallback(async () => {
    if (!renamePresetId) return;
    const nextName = renamePresetName.trim();
    if (!nextName) {
      toast({ title: 'Preset name required', description: 'Rename the preset before saving.', variant: 'destructive' });
      return;
    }

    setPresetActionLoading(`rename:${renamePresetId}`);
    try {
      await persistPresetPreferences({
        presetProfiles: presetProfiles.map((preset) =>
          preset.id === renamePresetId ? { ...preset, name: nextName } : preset,
        ),
      });
      setRenamePresetId(null);
      setRenamePresetName('');
    } catch (error: unknown) {
      toast({ title: 'Rename failed', description: String(error), variant: 'destructive' });
    } finally {
      setPresetActionLoading(null);
    }
  }, [persistPresetPreferences, presetProfiles, renamePresetId, renamePresetName, toast]);

  const handleDuplicatePreset = useCallback(async (preset: FocusPresetProfile) => {
    const duplicateName = `${preset.name} Copy`;
    const duplicatePreset: FocusPresetProfile = {
      id: createPresetId(),
      name: duplicateName,
      perPageFocusModes: { ...(preset.perPageFocusModes ?? {}) },
    };

    setPresetActionLoading(`duplicate:${preset.id}`);
    try {
      await persistPresetPreferences({
        presetProfiles: [...presetProfiles, duplicatePreset],
      });
      toast({ title: 'Preset duplicated', description: `${duplicateName} is ready to customize.` });
    } catch (error: unknown) {
      toast({ title: 'Duplicate failed', description: String(error), variant: 'destructive' });
    } finally {
      setPresetActionLoading(null);
    }
  }, [persistPresetPreferences, presetProfiles, toast]);

  const handleDeletePreset = useCallback(async () => {
    if (!deletePresetId) return;

    setPresetActionLoading(`delete:${deletePresetId}`);
    try {
      await persistPresetPreferences({
        presetProfiles: presetProfiles.filter((preset) => preset.id !== deletePresetId),
        ...(activePresetId === deletePresetId ? { activePresetId: null } : {}),
      });
      clearPresetUsage(deletePresetId);
      setDeletePresetId(null);
    } catch (error: unknown) {
      toast({ title: 'Delete failed', description: String(error), variant: 'destructive' });
    } finally {
      setPresetActionLoading(null);
    }
  }, [activePresetId, clearPresetUsage, deletePresetId, persistPresetPreferences, presetProfiles, toast]);

  const handleApplyPreset = useCallback(async () => {
    if (!previewPreset) return;

    setPresetActionLoading(`apply:${previewPreset.id}`);
    try {
      setApplyUndoState({
        activePresetId,
        perPageFocusModes: { ...(user?.preferences?.perPageFocusModes ?? {}) },
        presetName: previewPreset.name,
      });
      await persistPresetPreferences({
        activePresetId: previewPreset.id,
        perPageFocusModes: { ...(previewPreset.perPageFocusModes ?? {}) },
      });
      markPresetApplied(previewPreset.id);
      setPreviewPresetId(null);
      toast({ title: 'Preset applied', description: `${previewPreset.name} is now your active synced preset.` });
    } catch (error: unknown) {
      toast({ title: 'Apply failed', description: String(error), variant: 'destructive' });
    } finally {
      setPresetActionLoading(null);
    }
  }, [activePresetId, markPresetApplied, persistPresetPreferences, previewPreset, toast, user?.preferences?.perPageFocusModes]);

  const handleUndoPresetApply = useCallback(async () => {
    if (!applyUndoState) return;

    setPresetActionLoading('undo-apply');
    try {
      await persistPresetPreferences({
        activePresetId: applyUndoState.activePresetId,
        perPageFocusModes: { ...applyUndoState.perPageFocusModes },
      });
      toast({ title: 'Preset apply undone', description: 'Your previous page-level preferences are restored.' });
      setApplyUndoState(null);
    } catch (error: unknown) {
      toast({ title: 'Undo failed', description: String(error), variant: 'destructive' });
    } finally {
      setPresetActionLoading(null);
    }
  }, [applyUndoState, persistPresetPreferences, toast]);

  const previewRows = (
    <div className="space-y-3" data-testid="settings-preset-preview-list">
      {FOCUS_PAGE_ORDER.map((pageKey) => {
        const currentMode = effectiveFocusModes[pageKey];
        const nextMode = previewModes[pageKey];
        const changes = currentMode !== nextMode;
        return (
          <div
            key={pageKey}
            className="rounded-lg border bg-muted/20 p-3"
            data-testid={`settings-preset-preview-row-${pageKey}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium">{FOCUS_PAGE_LABELS[pageKey]}</p>
              <Badge
                variant={changes ? 'secondary' : 'outline'}
                data-testid={`settings-preset-preview-change-${pageKey}`}
              >
                {changes ? 'Will change' : 'No change'}
              </Badge>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>Current</span>
              <Badge variant="outline">{formatModeLabel(currentMode)}</Badge>
              <span>→</span>
              <Badge variant="secondary">{formatModeLabel(nextMode)}</Badge>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4 md:space-y-8" data-testid="settings-page">
      <PageGuide pageKey="settings" content={settingsGuide} />
      <div className="flex flex-col gap-2">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Settings</h2>
        <p className="text-sm md:text-base text-muted-foreground">Manage your account preferences and security</p>
      </div>

      {/* ── 1. Profile ─────────────────────────────────────────────────────── */}
      <Card className="transition-all duration-200 hover:shadow-md" data-testid="profile-card">
        <CardHeader className="flex flex-row flex-wrap items-center gap-4 pb-4">
          <div className="p-2 bg-primary/10 rounded-full">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle>Profile</CardTitle>
            <CardDescription>Update your personal information and preferences</CardDescription>
          </div>
          {user && !user.isEmailVerified && (
            <Badge variant="secondary" className="text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/30 shrink-0">
              Email not verified
            </Badge>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {user?.profilePicture && (
            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
              <img src={user.profilePicture} alt={user.fullName} className="h-12 w-12 rounded-full object-cover ring-2 ring-primary/20" />
              <div>
                <p className="text-sm font-medium">{user.fullName}</p>
                <p className="text-xs text-muted-foreground">{user.email}</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <div className="flex items-center gap-2">
              <Input value={user?.email || ''} disabled className="flex-1 bg-muted" />
              {user && !user.isEmailVerified && (
                <Button variant="outline" size="sm" onClick={handleResendVerification} disabled={verificationLoading}>
                  <Mail className="h-4 w-4 mr-1" />
                  {verificationLoading ? 'Sending…' : 'Verify'}
                </Button>
              )}
            </div>
          </div>
          <Separator />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Currency</Label>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" data-testid="current-currency-badge">
                  {user?.preferences?.currency || 'USD'}
                </Badge>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrencyWizardOpen(true)}
                  data-testid="open-currency-wizard"
                >
                  Change...
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Current display currency. Changing this will re-value all your money
                records and write an audit log entry.
              </p>
            </div>

            <div className="space-y-2 sm:col-span-2 lg:col-span-3">
              <CurrencyHistoryTimeline />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dateFormat">Date Format</Label>
              <Select value={dateFormat} onValueChange={setDateFormat}>
                <SelectTrigger id="dateFormat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                  <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                  <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="budgetMethod">Budget Method</Label>
              <Select value={budgetMethod} onValueChange={setBudgetMethod}>
                <SelectTrigger id="budgetMethod"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="zero-based">Zero-Based</SelectItem>
                  <SelectItem value="spending-plan">Spending Plan</SelectItem>
                  <SelectItem value="rollover">Rollover</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* [T114-F3-i] Timezone preference */}
          <div className="max-w-sm">
            <TimezoneSelector
              value={userTimezone}
              onValueChange={setUserTimezone}
              label="Timezone"
            />
            <p className="text-xs text-muted-foreground mt-1">
              All transaction dates and analytics will be displayed in this timezone
            </p>
          </div>
          <Separator />
          {/* T113-c-2b: Line items display toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="showLineItems">Show line items in transaction lists</Label>
              <p className="text-sm text-muted-foreground">Display expandable line item details in the transactions table and card view</p>
            </div>
            <Switch id="showLineItems" checked={showLineItemsInList} onCheckedChange={setShowLineItemsInList} />
          </div>
          {/* T117-3: Item count display toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="showItemCount">Show item count</Label>
              <p className="text-sm text-muted-foreground">Display the number of items in transaction details and analytics</p>
            </div>
            <Switch id="showItemCount" checked={showItemCount} onCheckedChange={toggleItemCount} />
          </div>
          <Separator />
          {/* [T92P2-d] Audit History Retention */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="audit-retention-setting">
            <div className="space-y-0.5">
              <Label htmlFor="auditRetention">Audit History Retention</Label>
              <p className="text-sm text-muted-foreground">Number of days to keep audit trail history (1–30)</p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                id="auditRetention"
                type="number"
                min={1}
                max={30}
                value={auditRetentionDays}
                onChange={(e) => {
                  const v = Math.min(30, Math.max(1, parseInt(e.target.value) || 1));
                  setAuditRetentionDays(v);
                }}
                className="w-20"
                data-testid="audit-retention-input"
              />
              <span className="text-sm text-muted-foreground">days</span>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleProfileSave} disabled={profileLoading}>
              {profileLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── 2. Appearance ──────────────────────────────────────────────────── */}
      <Card className="transition-all duration-200 hover:shadow-md" data-testid="appearance-card">
        <CardHeader className="flex flex-row items-center gap-4 pb-4">
          <div className="p-2 bg-primary/10 rounded-full">
            <Palette className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle>Appearance</CardTitle>
            <CardDescription>Choose how FinTrack looks on your device</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={theme}
            onValueChange={(v) => setTheme(v as 'light' | 'dark' | 'system')}
            className="grid grid-cols-1 sm:grid-cols-3 gap-3"
            data-testid="theme-selector"
          >
            {([
              { value: 'light',  label: 'Light',  Icon: Sun,     desc: 'Always use light theme' },
              { value: 'dark',   label: 'Dark',   Icon: Moon,    desc: 'Always use dark theme' },
              { value: 'system', label: 'System', Icon: Monitor, desc: 'Follow system preference' },
            ] as const).map(({ value, label, Icon, desc }) => (
              <Label
                key={value}
                htmlFor={`theme-${value}`}
                className={`flex flex-col items-center gap-2 rounded-lg border p-4 cursor-pointer transition-all ${
                  theme === value
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
              >
                <RadioGroupItem value={value} id={`theme-${value}`} className="sr-only" />
                <Icon className={`h-5 w-5 ${theme === value ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className={`text-sm font-medium ${theme === value ? 'text-primary' : ''}`}>{label}</span>
                <span className="text-xs text-muted-foreground text-center">{desc}</span>
                {theme === value && <Check className="h-3 w-3 text-primary" />}
              </Label>
            ))}
          </RadioGroup>
          <Separator className="mt-4" />
          {/* [T94P2] Reset FAB Position */}
          <div className="flex items-center justify-between mt-4">
            <div className="space-y-0.5">
              <Label>Floating Action Button</Label>
              <p className="text-sm text-muted-foreground">Reset the FAB position to default (bottom-right corner)</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => {
              localStorage.removeItem('fintrack:fab-position');
              toast({ title: 'FAB position reset', description: 'The button will return to its default position on the next page load.' });
            }}>
              Reset Position
            </Button>
          </div>
          <Separator className="mt-4" />
          {/* [T97P2] Theme Customization */}
          <div className="mt-4">
            <ThemeCustomizationPanel />
          </div>
        </CardContent>
      </Card>

      {/* ── 3. Notifications ────────────────────────────────────────────────── */}
      <Card className="transition-all duration-200 hover:shadow-md" data-testid="notifications-card">
        <CardHeader className="flex flex-row items-center gap-4 pb-4">
          <div className="p-2 bg-primary/10 rounded-full">
            <Bell className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <CardTitle>Notifications</CardTitle>
            <CardDescription>Configure how you receive notifications</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {([
            { id: 'emailNotif', label: 'Email Notifications', desc: 'Receive summary emails about your account activity', checked: emailNotifications, setChecked: setEmailNotifications },
            { id: 'budgetAlert', label: 'Budget Alerts', desc: 'Get notified when you approach or exceed your budget', checked: budgetAlerts, setChecked: setBudgetAlerts },
            { id: 'billRemind', label: 'Bill Reminders', desc: 'Reminders for upcoming bills and recurring payments', checked: billReminders, setChecked: setBillReminders },
          ] as const).map((item, idx, arr) => (
            <div key={item.id}>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor={item.id}>{item.label}</Label>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
                <Switch id={item.id} checked={item.checked} onCheckedChange={item.setChecked} />
              </div>
              {idx < arr.length - 1 && <Separator className="mt-4" />}
            </div>
          ))}
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={handleNotificationsSave} disabled={notifLoading}>
              {notifLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : 'Save Preferences'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── T155P5. Automation ──────────────────────────────────────────────── */}
      <Card className="transition-all duration-200 hover:shadow-md" data-testid="settings-automation-card">
        <CardHeader className="flex flex-row items-center gap-4 pb-4">
          <div className="p-2 bg-primary/10 rounded-full">
            <Wand2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle>Automation</CardTitle>
            <CardDescription>Control how transaction rules apply to your review inbox</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Auto-categorize toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="autoCategorize">Auto-categorize on import</Label>
              <p className="text-sm text-muted-foreground">
                Automatically run matching rules when transactions are imported (no confirmation prompt)
              </p>
            </div>
            <Switch
              id="autoCategorize"
              checked={autoCategorize}
              onCheckedChange={setAutoCategorize}
              data-testid="automation-toggle-auto-categorize"
            />
          </div>
          <Separator />

          {/* Show suggestions toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="showSuggestions">Show rule suggestions in Review inbox</Label>
              <p className="text-sm text-muted-foreground">
                Display suggestion chips and banners when automation rules match transactions
              </p>
            </div>
            <Switch
              id="showSuggestions"
              checked={showSuggestions}
              onCheckedChange={setShowSuggestions}
              data-testid="automation-toggle-show-suggestions"
            />
          </div>
          <Separator />

          {/* Require preview toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="requirePreview">Require preview before applying rules</Label>
              <p className="text-sm text-muted-foreground">
                Always show a before/after preview modal before committing rule changes
              </p>
              {!requirePreview && (
                <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1 mt-1">
                  <AlertTriangle className="size-3 shrink-0" />
                  Manual override is always available in Review and bulk flows can still force preview.
                </p>
              )}
            </div>
            <Switch
              id="requirePreview"
              checked={requirePreview}
              onCheckedChange={setRequirePreview}
              data-testid="automation-toggle-require-preview"
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <p className="text-xs text-muted-foreground">
              These defaults sync with your profile. Review Inbox keeps a visible manual override path.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="min-h-[44px] md:min-h-0"
              data-testid="automation-reset-defaults-btn"
              onClick={resetAutomationPreferences}
            >
              Reset Automation Defaults
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="transition-all duration-200 hover:shadow-md" data-testid="settings-preset-manager-card">
        <CardHeader className="flex flex-row items-center gap-4 pb-4">
          <div className="p-2 bg-primary/10 rounded-full">
            <Rows3 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle>Focus presets</CardTitle>
            <CardDescription>Keep focus layouts portable across devices without hiding page-level overrides.</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {applyUndoState ? (
            <Alert data-testid="settings-preset-undo-banner">
              <Undo2 className="h-4 w-4" />
              <AlertTitle>{applyUndoState.presetName} applied</AlertTitle>
              <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <span>The preset is active now. Undo restores your previous per-page focus setup.</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-h-[44px] self-start sm:self-auto"
                  onClick={() => void handleUndoPresetApply()}
                  disabled={presetActionLoading === 'undo-apply'}
                  data-testid="settings-preset-undo-btn"
                >
                  {presetActionLoading === 'undo-apply' ? 'Undoing…' : 'Undo'}
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}

          {hasLegacyPresetFallback ? (
            <Alert data-testid="settings-preset-migration-callout">
              <Wand2 className="h-4 w-4" />
              <AlertTitle>Bring your local focus setup with you</AlertTitle>
              <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <span>Your browser still has page-specific focus preferences. Create a synced preset so other devices can use them too.</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-h-[44px] self-start sm:self-auto"
                  onClick={() => void handleCreatePreset({
                    activate: true,
                    seedModes: effectiveFocusModes,
                    name: 'My portable preset',
                  })}
                  disabled={presetActionLoading?.startsWith('create:')}
                  data-testid="settings-preset-migrate-btn"
                >
                  Create portable preset
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="rounded-lg border bg-muted/20 p-3" data-testid="settings-preset-create-box">
            <div className="flex flex-col gap-3 md:flex-row md:items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="presetName">New preset name</Label>
                <Input
                  id="presetName"
                  value={createPresetName}
                  onChange={(event) => setCreatePresetName(event.target.value)}
                  placeholder="Weekend review setup"
                  data-testid="settings-preset-name-input"
                />
              </div>
              <Button
                type="button"
                className="min-h-[44px]"
                onClick={() => void handleCreatePreset()}
                disabled={!createPresetName.trim()}
                data-testid="settings-preset-create-btn"
              >
                Save current setup as preset
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              New presets capture the current focus mode on Dashboard, Transactions, Review, and Analytics.
            </p>
          </div>

          {presetProfiles.length === 0 ? (
            <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground" data-testid="settings-preset-empty-state">
              No synced focus presets yet. Save your current page setup to make it portable across devices.
            </div>
          ) : (
            <div className="space-y-3" data-testid="settings-preset-list">
              {presetProfiles.map((preset) => {
                const isActive = preset.id === activePresetId;
                const lastAppliedAt = getPresetLastApplied(preset.id);
                const summary = FOCUS_PAGE_ORDER.map((pageKey) => (
                  `${FOCUS_PAGE_LABELS[pageKey]}: ${formatModeLabel(
                    preset.perPageFocusModes?.[pageKey] ?? effectiveFocusModes[pageKey],
                  )}`
                )).join(' • ');

                return (
                  <div
                    key={preset.id}
                    className="rounded-lg border p-3"
                    data-testid={`settings-preset-row-${preset.id}`}
                  >
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div className="space-y-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium break-words">{preset.name}</p>
                          {isActive ? (
                            <Badge variant="secondary" data-testid={`settings-preset-active-badge-${preset.id}`}>
                              Active
                            </Badge>
                          ) : null}
                        </div>
                        <p
                          className="text-xs text-muted-foreground italic"
                          data-testid={`preset-last-applied-${preset.id}`}
                        >
                          {lastAppliedAt
                            ? `Last applied: ${formatRelativeTime(lastAppliedAt)}`
                            : 'Never applied'}
                        </p>
                        <p className="text-xs text-muted-foreground break-words">{summary}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant={isActive ? 'secondary' : 'outline'}
                          size="sm"
                          className="min-h-[44px]"
                          onClick={() => setPreviewPresetId(preset.id)}
                          disabled={presetActionLoading === `apply:${preset.id}`}
                          data-testid={`settings-preset-apply-btn-${preset.id}`}
                        >
                          {isActive ? 'Preview apply' : 'Apply'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="min-h-[44px]"
                          onClick={() => {
                            setRenamePresetId(preset.id);
                            setRenamePresetName(preset.name);
                          }}
                          data-testid={`settings-preset-rename-btn-${preset.id}`}
                        >
                          <Pencil className="mr-1.5 h-3.5 w-3.5" />Rename
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="min-h-[44px]"
                          onClick={() => void handleDuplicatePreset(preset)}
                          disabled={presetActionLoading === `duplicate:${preset.id}`}
                          data-testid={`settings-preset-duplicate-btn-${preset.id}`}
                        >
                          <Copy className="mr-1.5 h-3.5 w-3.5" />Duplicate
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="min-h-[44px]"
                          onClick={() => setDeletePresetId(preset.id)}
                          data-testid={`settings-preset-delete-btn-${preset.id}`}
                        >
                          <Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── 4. Active Sessions ──────────────────────────────────────────────── */}
      <Card className="transition-all duration-200 hover:shadow-md" data-testid="sessions-card">
        <CardHeader className="flex flex-row flex-wrap items-center gap-4 pb-4">
          <div className="p-2 bg-primary/10 rounded-full">
            <Globe className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle>Active Sessions</CardTitle>
            <CardDescription>Devices and locations currently signed into your account</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={handleLogoutAllOther}
            disabled={logoutAllLoading || sessions.length <= 1}
          >
            {logoutAllLoading
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <><LogOut className="h-4 w-4 mr-1" />Sign out all</>}
          </Button>
        </CardHeader>
        <CardContent>
          {sessionsLoading ? (
            <div className="space-y-3">{[1, 2].map((i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}</div>
          ) : sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No active sessions found</p>
          ) : (
            <div className="space-y-3">
              {sessions.map((session, idx) => (
                <div key={session.id} className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                  <div className="mt-0.5">
                    <DeviceIcon deviceType={session.deviceInfo?.deviceType} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">
                        {session.deviceInfo?.browser || 'Unknown Browser'}
                        {session.deviceInfo?.os ? ` on ${session.deviceInfo.os}` : ''}
                      </span>
                      {idx === 0 && (
                        <Badge variant="secondary" className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-400">
                          Current
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {session.ipAddress && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />{session.ipAddress}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        Last active {formatDistanceToNow(new Date(session.lastUsed), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                  {idx !== 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                      onClick={() => handleRevokeSession(session.id)}
                      disabled={revokingId === session.id}
                    >
                      {revokingId === session.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Revoke'}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── 5. Login Activity ───────────────────────────────────────────────── */}
      <Card className="transition-all duration-200 hover:shadow-md" data-testid="security-events-card">
        <CardHeader className="flex flex-row items-center gap-4 pb-4">
          <div className="p-2 bg-primary/10 rounded-full">
            <History className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle>Login Activity</CardTitle>
            <CardDescription>Recent account access and security events</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {securityEventsLoading ? (
            <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}</div>
          ) : securityEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No recent activity</p>
          ) : (
            <div className="space-y-1">
              {securityEvents.map((event, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <EventBadge event={event} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{event.message || event.eventType.replace(/_/g, ' ')}</p>
                    {event.ipAddress && (
                      <p className="text-xs text-muted-foreground">{event.ipAddress}</p>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {format(new Date(event.timestamp), 'MMM d, HH:mm')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── 6. Connected Accounts ───────────────────────────────────────────── */}
      {googleEnabled && (
        <Card className="transition-all duration-200 hover:shadow-md" data-testid="connected-accounts-card">
          <CardHeader className="flex flex-row items-center gap-4 pb-4">
            <div className="p-2 bg-primary/10 rounded-full">
              <Link className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>Connected Accounts</CardTitle>
              <CardDescription>Link third-party accounts for faster sign-in</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <GoogleConnectSection isConnected={isGoogleConnected} hasLocalPassword={hasLocalPassword} />
            {isGoogleConnected && !hasLocalPassword && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Google is your only sign-in method. Set a password in the Security section before disconnecting.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── 7. Security ─────────────────────────────────────────────────────── */}
      <Card className="transition-all duration-200 hover:shadow-md" data-testid="security-card">
        <CardHeader className="flex flex-row items-center gap-4 pb-4">
          <div className="p-2 bg-primary/10 rounded-full">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle>Security</CardTitle>
            <CardDescription>Manage your account security settings</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-muted-foreground" />
              <Label className="text-base font-medium">Change Password</Label>
              {!hasLocalPassword && (
                <Badge variant="secondary" className="text-xs">Google-only account</Badge>
              )}
            </div>
            {!hasLocalPassword ? (
              <p className="text-sm text-muted-foreground pl-6">
                Your account uses Google sign-in. Use "Forgot Password" to set a password.
              </p>
            ) : (
              <div className="grid gap-3 pl-6">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input id="currentPassword" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input id="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min. 8 characters" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repeat new password" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button variant="outline" onClick={handlePasswordChange} disabled={passwordLoading || !currentPassword || !newPassword}>
                    {passwordLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Changing…</> : 'Update Password'}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <Separator />
          <div className="space-y-2">
            <Label className="text-base font-medium">Two-Factor Authentication</Label>
            <p className="text-sm text-muted-foreground">
              Add an extra layer of security using an authenticator app (TOTP / RFC 6238).
            </p>
            <Button variant="outline" disabled>
              Enable 2FA (Coming Soon)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── 8. Data & Privacy ───────────────────────────────────────────────── */}
      <Card className="transition-all duration-200 hover:shadow-md" data-testid="settings-gdpr-section">
        <CardHeader className="flex flex-row items-center gap-4 pb-4">
          <div className="p-2 bg-primary/10 rounded-full">
            <Download className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle>Data &amp; Privacy</CardTitle>
            <CardDescription>Export or import your personal financial data</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* [T155P3] Data Health Hub — overview before export/delete actions */}
          <div data-testid="settings-health-panel">
            <DataHealthPanel />
          </div>

          {/* Receipt Storage Stats */}
          <div data-testid="receipt-storage-stats" className="grid grid-cols-2 gap-3 mb-4">
            <div className="glass-panel rounded-lg p-4 text-center">
              <p className="text-2xl font-bold" data-testid="receipt-stats-count">
                {receiptStats ? receiptStats.count : '—'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Receipts stored</p>
            </div>
            <div className="glass-panel rounded-lg p-4 text-center">
              <p className="text-2xl font-bold" data-testid="receipt-stats-size">
                {receiptStats ? formatBytes(receiptStats.totalSize) : '—'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Storage used</p>
            </div>
          </div>

          {/* Export */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="space-y-0.5">
              <Label>Export Data</Label>
              <p className="text-sm text-muted-foreground">
                Download selected scopes as a portable JSON file (GDPR compliant)
              </p>
            </div>
            <ExportDialog />
          </div>

          <Separator />

          {/* Transaction-specific export */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="space-y-0.5">
              <Label>Export Transactions</Label>
              <p className="text-sm text-muted-foreground">
                Export transactions with filters, summary preview, and CSV/JSON format options
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/transactions')}>
              <Download className="h-4 w-4 mr-2" />
              Go to Transactions
            </Button>
          </div>

          <Separator />

          {/* Import */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="space-y-0.5">
              <Label>Import Data</Label>
              <p className="text-sm text-muted-foreground">
                Restore data from a FinTrack export file (e.g., from another environment)
              </p>
            </div>
            <ImportDialog />
          </div>

          <Separator />

          {/* Receipt Bulk Export */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="space-y-0.5">
              <Label>Export Receipts</Label>
              <p className="text-sm text-muted-foreground">
                Download signed URLs for all your uploaded receipts (GDPR compliant)
              </p>
            </div>
            <Button variant="outline" data-testid="receipt-bulk-export-btn" onClick={() => window.open('/api/v1/receipts/export', '_blank')}>
              <Download className="h-4 w-4 mr-2" />
              Export Receipts
            </Button>
          </div>
        </CardContent>
      </Card>

      {isMobile ? (
        <Sheet open={!!previewPresetId} onOpenChange={(open) => !open && setPreviewPresetId(null)}>
          <SheetContent
            side="bottom"
            className="max-h-[85dvh] overflow-y-auto"
            data-testid="settings-preset-preview-sheet"
          >
            <SheetHeader>
              <SheetTitle>Preview preset apply</SheetTitle>
              <SheetDescription>
                {previewPreset
                  ? `Check how ${previewPreset.name} will update each focus-aware page before you make it active.`
                  : 'Choose a preset to preview.'}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-4">{previewRows}</div>
            <SheetFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setPreviewPresetId(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void handleApplyPreset()}
                disabled={!previewPreset || presetActionLoading === `apply:${previewPreset?.id}`}
                data-testid="settings-preset-preview-confirm-btn"
              >
                {previewPreset && presetActionLoading === `apply:${previewPreset.id}` ? 'Applying…' : 'Apply preset'}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={!!previewPresetId} onOpenChange={(open) => !open && setPreviewPresetId(null)}>
          <DialogContent data-testid="settings-preset-preview-dialog">
            <DialogHeader>
              <DialogTitle>Preview preset apply</DialogTitle>
              <DialogDescription>
                {previewPreset
                  ? `Check how ${previewPreset.name} will update each focus-aware page before you make it active.`
                  : 'Choose a preset to preview.'}
              </DialogDescription>
            </DialogHeader>
            {previewRows}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPreviewPresetId(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void handleApplyPreset()}
                disabled={!previewPreset || presetActionLoading === `apply:${previewPreset?.id}`}
                data-testid="settings-preset-preview-confirm-btn"
              >
                {previewPreset && presetActionLoading === `apply:${previewPreset.id}` ? 'Applying…' : 'Apply preset'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={!!renamePresetId} onOpenChange={(open) => !open && setRenamePresetId(null)}>
        <DialogContent data-testid="settings-preset-rename-dialog">
          <DialogHeader>
            <DialogTitle>Rename preset</DialogTitle>
            <DialogDescription>Update the label shown in Settings and on your synced devices.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="renamePreset">Preset name</Label>
            <Input
              id="renamePreset"
              value={renamePresetName}
              onChange={(event) => setRenamePresetName(event.target.value)}
              data-testid="settings-preset-rename-input"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRenamePresetId(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleRenamePreset()}
              disabled={!renamePresetName.trim() || (renamePresetId ? presetActionLoading === `rename:${renamePresetId}` : false)}
              data-testid="settings-preset-rename-save-btn"
            >
              Save name
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deletePresetId} onOpenChange={(open) => !open && setDeletePresetId(null)}>
        <DialogContent data-testid="settings-preset-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete preset</DialogTitle>
            <DialogDescription>
              Delete this synced preset? Your current page-level focus modes stay intact.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeletePresetId(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleDeletePreset()}
              disabled={deletePresetId ? presetActionLoading === `delete:${deletePresetId}` : false}
              data-testid="settings-preset-delete-confirm-btn"
            >
              Delete preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── 9. Danger Zone ──────────────────────────────────────────────────── */}
      <Card className="border-destructive transition-all duration-200 hover:shadow-md" data-testid="delete-account-section">
        <CardHeader className="flex flex-row items-center gap-4 pb-4">
          <div className="p-2 bg-destructive/10 rounded-full">
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </div>
          <div>
            <CardTitle className="text-destructive">Danger Zone</CardTitle>
            <CardDescription>Irreversible account actions</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="destructive" data-testid="delete-account-btn">Delete Account</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-destructive">Delete Account</DialogTitle>
                <DialogDescription>
                  This action is permanent and cannot be undone. All your data — accounts, transactions,
                  budgets, goals, and categories — will be permanently deleted.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-4">
                {deletePreviewLoading && (
                  <div data-testid="delete-preview-loading" className="rounded-md border bg-muted/30 p-3 text-sm">
                    <Loader2 className="h-4 w-4 mr-2 inline animate-spin" />
                    Loading deletion summary…
                  </div>
                )}
                {deletePreview && (
                  <div
                    data-testid="delete-preview-summary"
                    className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm space-y-2"
                  >
                    <p className="font-medium">This will permanently delete:</p>
                    <ul className="space-y-1 list-disc pl-5">
                      <li>{formatNumber(deletePreview.collections.transactions)} transactions</li>
                      <li>{formatNumber(deletePreview.collections.accounts)} accounts</li>
                      <li>
                        {formatNumber(deletePreview.collections.budgets)} budgets,{' '}
                        {formatNumber(deletePreview.collections.goals)} goals
                      </li>
                      <li>
                        {formatNumber(deletePreview.collections.receipts)} receipts (
                        {formatBytes(deletePreview.receiptFileBytes)})
                      </li>
                      <li>
                        {formatNumber(deletePreview.collections.subscriptions)} subscriptions,{' '}
                        {formatNumber(deletePreview.collections.dashboards)} dashboards
                      </li>
                      <li>
                        {formatNumber(deletePreview.totalRecords)} total records across 16 collections
                      </li>
                    </ul>
                    <p className="text-xs text-muted-foreground">This action cannot be undone.</p>
                  </div>
                )}
                <Label htmlFor="deletePassword">Enter your password to confirm</Label>
                <Input id="deletePassword" type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} placeholder="Your password" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteAccount}
                  disabled={deleteLoading || !deletePassword || deletePreviewLoading || !deletePreview}
                  data-testid="delete-confirm-btn"
                >
                  {deleteLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting…</> : 'Permanently Delete'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
      <CurrencyChangeWizard
        open={currencyWizardOpen}
        onOpenChange={setCurrencyWizardOpen}
        onComplete={() => undefined}
      />
    </div>
  );
}
